import React, { useEffect, useState } from 'react';
import {
  percentageLabel,
  displayStatus,
  evaluationPercentage,
} from '../../lib/evaluationIntegrity';
import { ScoreSummary } from '../../components/EvaluationIntegrity';
import { useNavigate } from 'react-router-dom';
import { evaluationsAPI, projectsAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import SemaforoBadge from '../../components/SemaforoBadge';
import StatusBadge from '../../components/StatusBadge';
import { SectionRadarChart } from '../../components/ScoreChart';
import { getSectionStatsFromEval } from '../../lib/evalSections';
import { AlertTriangle, FolderKanban, Clock, CheckCircle2, ArrowRight, GitCompare, Calendar } from 'lucide-react';

function getDeadlineStatus(deadline, hasPublished) {
  if (!deadline) return null;
  const today = new Date();
  const due = new Date(deadline);
  const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  // #OfWkuj2X — antes la rama "Vencido hace X día(s)" solo aplicaba
  // si !hasPublished. Pero diffDays<=7 también matchea cuando diffDays
  // es negativo, así que un proyecto publicado y vencido caía ahí y
  // pintaba "Vence en -54 día(s)". Ahora la rama de diffDays<0 atrapa
  // todos los casos negativos: si está publicado mostramos un badge
  // verde "Concluido (vencido)"; si no, el badge rojo de vencido.
  if (diffDays < 0) {
    if (hasPublished) {
      return { label: 'Concluido', color: 'bg-green-100 text-green-700 border-green-200', urgent: false };
    }
    return { label: `Vencido hace ${Math.abs(diffDays)} día(s)`, color: 'bg-red-100 text-red-700 border-red-200', urgent: true };
  }
  if (diffDays === 0) return { label: 'Vence hoy', color: 'bg-orange-100 text-orange-700 border-orange-200', urgent: true };
  if (diffDays <= 7) return { label: `Vence en ${diffDays} día(s)`, color: 'bg-yellow-100 text-yellow-700 border-yellow-200', urgent: true };
  return null;
}

export default function LiderDashboard() {
  const { activeUser } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [radarEval, setRadarEval] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([projectsAPI.list(), evaluationsAPI.list()])
      .then(async ([p, e]) => {
        setProjects(p.data.items || []);
        const evs = e.data.items || [];
        setEvaluations(evs);
        // El radar necesita las secciones con sus puntajes, y el LISTADO no
        // devuelve `items` ni `sections` (la proyección los excluye): el
        // radar salía siempre colapsado en 0%. Se pide el detalle solo de la
        // evaluación que se grafica.
        const ultima = evs
          .filter(x => x.status === 'publicada')
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
        if (ultima) {
          try { setRadarEval((await evaluationsAPI.get(ultima.id)).data); } catch { /* el radar simplemente no se pinta */ }
        }
      })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const pendingReview = evaluations.filter(e => e.status === 'en_revision');
  // Bloqueadas = en_revision con criterio bloqueante + evaluaciones
  // regresadas al Levantador (#ebUmKC1d).
  const blocked = evaluations.filter(e =>
    (e.is_blocked && e.status === 'en_revision') || e.status === 'regresada'
  );
  const myProjects = projects.filter(p => p.status === 'activo');

  // Deadline alerts (#OfWkuj2X) — además de los urgentes, mostramos un
  // resumen con todos los proyectos que tienen deadline próximo en una
  // tarjeta dedicada más abajo.
  const publishedProjectIds = new Set(evaluations.filter(e => e.status === 'publicada').map(e => e.project_id));
  const projectsWithDeadlines = myProjects
    .map(p => ({ ...p, deadlineStatus: getDeadlineStatus(p.deadline, publishedProjectIds.has(p.id)) }))
    .filter(p => p.deadline);
  const deadlineAlerts = projectsWithDeadlines.filter(p => p.deadlineStatus?.urgent);
  // Resumen completo de vencimientos ordenado por fecha.
  const allDeadlines = [...projectsWithDeadlines].sort((a, b) =>
    new Date(a.deadline) - new Date(b.deadline)
  );

  // Best evaluation for radar (latest published)
  const latestPublished = radarEval;
  const radarStats = latestPublished ? getSectionStatsFromEval(latestPublished) : null;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lider-dashboard" id="dashboard-lider">
      <div>
        <h1 className="text-2xl font-bold text-soem-navy">Dashboard — Líder</h1>
        <p className="text-slate-500 text-sm mt-1">Bienvenido, {activeUser?.name}</p>
      </div>

      {blocked.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4 flex items-center gap-3" data-testid="blocked-alert">
          <AlertTriangle size={20} className="text-red-700 flex-shrink-0" />
          <div>
            <p className="font-semibold text-red-800">{blocked.length} evaluación(es) con criterios BLOQUEANTES</p>
            <p className="text-red-700 text-sm">Requieren revisión urgente antes de poder ser aprobadas.</p>
          </div>
        </div>
      )}

      {/* Deadline alerts */}
      {deadlineAlerts.length > 0 && (
        <div className="space-y-2" data-testid="deadline-alerts">
          {deadlineAlerts.map(p => (
            <div key={p.id}
              className={`border rounded-sm p-3 flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity ${p.deadlineStatus.color.includes('red') ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}
              onClick={() => navigate(`/dashboard/proyectos/${p.id}`)}>
              <div className="flex items-center gap-2">
                <Calendar size={16} className={p.deadlineStatus.color.includes('red') ? 'text-red-700' : 'text-yellow-600'} />
                <span className={`font-semibold text-sm ${p.deadlineStatus.color.includes('red') ? 'text-red-800' : 'text-yellow-800'}`}>
                  {p.name}
                </span>
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${p.deadlineStatus.color}`}>
                {p.deadlineStatus.label}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          { label: 'Proyectos Activos', value: myProjects.length, color: '#1A56DB', icon: FolderKanban },
          { label: 'En Revisión', value: pendingReview.length, color: '#D97706', icon: Clock },
          { label: 'Bloqueadas', value: blocked.length, color: '#DC2626', icon: AlertTriangle },
        ].map(s => (
          <div key={s.label} className="soem-card p-5 border-t-4" style={{ borderTopColor: s.color }}>
            <div className="flex items-center justify-between">
              <div><p className="soem-label">{s.label}</p><p className="text-3xl font-black text-soem-navy mt-1">{s.value}</p></div>
              <s.icon size={28} strokeWidth={1.5} style={{ color: s.color }} />
            </div>
          </div>
        ))}
      </div>

      {/* Vencimientos de proyectos (#OfWkuj2X). */}
      {allDeadlines.length > 0 && (
        <div className="soem-card" data-testid="deadline-summary">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-soem-navy flex items-center gap-2">
              <Calendar size={16} /> Vencimientos de proyectos
            </h2>
            <span className="text-xs text-slate-400">{allDeadlines.length} con fecha límite</span>
          </div>
          <div className="divide-y divide-slate-50">
            {allDeadlines.slice(0, 8).map(p => {
              const due = new Date(p.deadline);
              const today = new Date();
              const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
              let badge;
              if (publishedProjectIds.has(p.id)) {
                badge = { label: 'Publicada ✓', cls: 'bg-green-100 text-green-700 border-green-200' };
              } else if (diff < 0) {
                badge = { label: `Vencido hace ${Math.abs(diff)} día(s)`, cls: 'bg-red-100 text-red-700 border-red-200' };
              } else if (diff === 0) {
                badge = { label: 'Vence hoy', cls: 'bg-orange-100 text-orange-700 border-orange-200' };
              } else if (diff <= 7) {
                badge = { label: `Vence en ${diff} día(s)`, cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
              } else {
                badge = { label: `En ${diff} día(s)`, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
              }
              return (
                <div key={p.id} className="flex items-center justify-between px-5 py-2.5 hover:bg-blue-50/50 cursor-pointer"
                  onClick={() => navigate(`/dashboard/proyectos/${p.id}`)}>
                  <div className="min-w-0">
                    <p className="font-semibold text-soem-navy text-sm truncate">{p.name}</p>
                    <p className="text-xs text-slate-400">Deadline: {due.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${badge.cls}`}>
                    {badge.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending review */}
        <div className="lg:col-span-2 soem-card" id="evaluaciones-por-revisar" data-testid="pending-review-card">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-soem-navy">Evaluaciones por Revisar</h2>
            {pendingReview.length > 0 && <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{pendingReview.length}</span>}
          </div>
          <div className="divide-y divide-slate-50">
            {pendingReview.length === 0 ? (
              <div className="flex items-center gap-2 p-5 text-slate-400"><CheckCircle2 size={18} /><span className="text-sm">No hay evaluaciones pendientes.</span></div>
            ) : (
              pendingReview.map(e => (
                <div key={e.id} className="flex items-center justify-between px-5 py-3 hover:bg-blue-50/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/dashboard/evaluaciones/${e.id}/revision`)} data-testid={`eval-review-${e.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-soem-navy text-sm">{e.general_fields?.instalacion || 'Sin nombre'} <span className="font-mono text-xs text-slate-400">v{e.version}</span></p>
                      {e.is_blocked && <span className="bg-red-100 text-red-700 text-xs px-1.5 py-0.5 rounded font-bold flex items-center gap-1"><AlertTriangle size={10} /> BLOQUEADA</span>}
                    </div>
                    <StatusBadge status={displayStatus(e)} />
                    <ScoreSummary evaluation={e} />
                  </div>
                  <div className="flex items-center gap-2">
                    <SemaforoBadge semaforo={e.semaforo} percentage={evaluationPercentage(e)} size="sm" />
                    <ArrowRight size={16} className="text-slate-300" />
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="p-5 border-t border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-soem-navy">Mis Proyectos</h2>
            <button onClick={() => navigate('/dashboard/proyectos')} className="text-soem-primary text-xs font-semibold hover:underline">Ver todos</button>
          </div>
          <div className="divide-y divide-slate-50">
            {myProjects.length === 0 ? (
              <p className="text-slate-400 text-sm p-5">No tienes proyectos asignados.</p>
            ) : (
              myProjects.slice(0, 4).map(p => (
                <div key={p.id} className="flex items-center justify-between px-5 py-3 hover:bg-blue-50/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/dashboard/proyectos/${p.id}`)} data-testid={`project-lider-${p.id}`}>
                  <p className="font-semibold text-soem-navy text-sm">{p.name}</p>
                  <div className="flex items-center gap-2">
                    <button onClick={e => { e.stopPropagation(); navigate(`/dashboard/proyectos/${p.id}/comparar`); }}
                      className="text-xs text-soem-primary hover:underline flex items-center gap-1" title="Comparar versiones">
                      <GitCompare size={13} /> Comparar
                    </button>
                    <ArrowRight size={16} className="text-slate-300" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Radar chart */}
        <div className="soem-card p-5">
          <p className="font-bold text-soem-navy mb-1">Última Evaluación Publicada</p>
          {radarStats ? (
            <>
              <p className="text-slate-400 text-xs mb-2">{latestPublished.general_fields?.instalacion || 'Sin nombre'} v{latestPublished.version}</p>
              <div className="mb-2">
                <SemaforoBadge semaforo={latestPublished.semaforo}
                  percentage={evaluationPercentage(latestPublished)} size="sm" />
              </div>
              <SectionRadarChart sectionStats={radarStats} size={220} />
              <div className="mt-2 space-y-1.5">
                {Object.entries(radarStats).map(([sec, s]) => (
                  <div key={sec} className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-600">{s.title || sec}</span>
                    <span className="font-mono text-slate-500">
                      {s.score}/{s.max} ({percentageLabel(s.percentage, 0)})
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No hay evaluaciones publicadas.</div>
          )}
        </div>
      </div>
    </div>
  );
}
